import React, { useMemo } from 'react';
import T from 'prop-types';
import { isEmpty } from 'lodash-es';

import HeatmapTable, { HeatmapEmptyState, HeatmapTableSkeleton } from '../HeatmapTable';
import { format } from '../../../../../utils/date';

const MAX_DISPLAYED_MONTHS = 8;

const ShareHeatmap = ({ metricSeries, apps, isPending, maxDate }) => {
  const { months, data } = useMemo(() => {
    const rows = apps.map((app) => {
      const fullSeries = metricSeries[app.unified_ref_no] || [];
      const sliceStartIndex = Math.max(fullSeries.length - MAX_DISPLAYED_MONTHS, 0);
      return {
        app,
        series: fullSeries.slice(sliceStartIndex),
      };
    });

    const isAnyRowDataPresent = rows.some(({ series }) => !isEmpty(series));

    if (!isAnyRowDataPresent) return { months: [], data: [] };

    const firstNonEmptyRow = rows.find(({ series }) => !isEmpty(series));

    const paddedRows = rows.map((row) => {
      if (isEmpty(row.series)) {
        return {
          app: row.app,
          series: firstNonEmptyRow.series.map(([ts]) => [ts, null]),
        };
      }

      return row;
    });

    const derivedMonths = firstNonEmptyRow.series.map(([ts]) => format(ts));

    return {
      months: derivedMonths,
      data: paddedRows.map(({ app, series }) => ({
        app,
        values: series.map(([, value]) => value),
      })),
    };
  }, [metricSeries, apps]);

  if (isPending) {
    return <HeatmapTableSkeleton height={496} className="b-t" />;
  }

  if (isEmpty(months) || isEmpty(data)) {
    return <HeatmapEmptyState className="b-t" />;
  }

  return (
    <div className="share-heatmap-container">
      <HeatmapTable
        months={months}
        data={data}
        maxDate={maxDate}
        className="share-heatmap"
        leadHeader={<div className="d-flex align-items-center">App</div>}
        getRowKey={({ app }) => app.unified_ref_no}
        renderLeadCell={({ app }) => (
          <td className="share-heatmap__app-cell text-truncate">{app.nickname || app.name}</td>
        )}
      />
    </div>
  );
};

ShareHeatmap.propTypes = {
  metricSeries: T.object.isRequired,
  apps: T.array.isRequired,
  isPending: T.bool.isRequired,
  maxDate: T.string.isRequired,
};

export default ShareHeatmap;
